import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import AppConfig from '../config/appConfig'; // path আপনার ফোল্ডার স্ট্রাকচার অনুযায়ী ঠিক করুন

function LoginPage() {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  const [showOtpInput, setShowOtpInput] = useState(false);
  const [showPasswordInput, setShowPasswordInput] = useState(false);

  const [isLoginMode, setIsLoginMode] = useState(true);
  const [isForgotPasswordMode, setIsForgotPasswordMode] = useState(false);

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);

  const navigate = useNavigate();
  const { login, getAuthHeaders, storeUserInfo } = useAuth();

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };
  const toggleNewPasswordVisibility = () => {
    setShowNewPassword(!showNewPassword);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsLoading(true);

    if (!/^\d{11}$/.test(phoneNumber)) {
      setError('Please enter a valid 11-digit mobile number.');
      setIsLoading(false);
      return;
    }
    if (!password.trim()) {
      setError('Password cannot be empty.');
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch(`${AppConfig.API_BASE_URL}/login`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ phone_number: phoneNumber, password: password }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(data.message);
        login(data.access_token, data.user_info);
        navigate('/');
      } else {
        setError(data.error || 'Login failed.');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError('Unable to connect to server.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setShowOtpInput(false);
    setShowPasswordInput(false);
    setIsLoading(true);

    if (!/^\d{11}$/.test(phoneNumber)) {
      setError('Please enter a valid 11-digit mobile number.');
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch(`${AppConfig.API_BASE_URL}/send_otp`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ phone_number: phoneNumber }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(data.message + " Please wait for your OTP.");
        setShowOtpInput(true);
      } else {
        setError(data.error || 'Failed to send OTP.');
      }
    } catch (err) {
      console.error('OTP send error:', err);
      setError('Unable to connect to server.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsLoading(true);

    if (!otpCode.trim()) {
      setError('OTP code cannot be empty.');
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch(`${AppConfig.API_BASE_URL}/verify_otp`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ phone_number: phoneNumber, otp_code: otpCode }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(data.message);
        if (data.is_registered) {
          login(data.access_token, data.user_info);
          navigate('/');
        } else {
          setShowPasswordInput(true);
        }
      } else {
        setError(data.error || 'OTP verification failed.');
      }
    } catch (err) {
      console.error('OTP verification error:', err);
      setError('Unable to connect to server.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCompleteRegistration = async (e, autoGenerate = false) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsLoading(true);

    let actualPassword = password;
    let endpoint = `${AppConfig.API_BASE_URL}/complete_registration`;
    let bodyData = { phone_number: phoneNumber };

    if (autoGenerate) {
      endpoint = `${AppConfig.API_BASE_URL}/register_with_auto_password`;
    } else {
      if (!actualPassword.trim()) {
        setError('Password cannot be empty.');
        setIsLoading(false);
        return;
      }
      bodyData.password = actualPassword;
    }

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(bodyData),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(data.message);
        login(data.access_token, data.user_info);
        navigate('/');
      } else {
        setError(data.error || 'Failed to complete registration.');
      }
    } catch (err) {
      console.error('Registration completion error:', err);
      setError('Unable to connect to server.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPasswordSendOtp = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setNewPassword('');
    setIsLoading(true);

    if (!/^\d{11}$/.test(phoneNumber)) {
      setError('Please enter a valid 11-digit mobile number.');
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch(`${AppConfig.API_BASE_URL}/forgot_password_send_otp`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ phone_number: phoneNumber }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(data.message + " Please wait for your OTP.");
        setShowOtpInput(true);
        setShowPasswordInput(false);
      } else {
        setError(data.error || 'Failed to send OTP.');
      }
    } catch (err) {
      console.error('Password reset OTP send error:', err);
      setError('Unable to connect to server.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPasswordVerifyOtp = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setResettingPassword(true);

    if (!otpCode.trim() || !newPassword.trim()) {
      setError('Both OTP code and new password are required.');
      setResettingPassword(false);
      return;
    }

    try {
      const response = await fetch(`${AppConfig.API_BASE_URL}/reset_password_verify_otp`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ phone_number: phoneNumber, otp_code: otpCode, new_password: newPassword }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(data.message + " You can now login with your new password.");
        setIsForgotPasswordMode(false);
        setIsLoginMode(true);
        setPhoneNumber('');
        setOtpCode('');
        setPassword('');
        setNewPassword('');
        setShowOtpInput(false);
        setShowPasswordInput(false);
      } else {
        setError(data.error || 'Password reset failed.');
      }
    } catch (err) {
      console.error('Password reset verification error:', err);
      setError('Unable to connect to server.');
    } finally {
      setResettingPassword(false);
    }
  };

  const handleToggleMode = () => {
    setIsLoginMode(!isLoginMode);
    setIsForgotPasswordMode(false);
    setPhoneNumber('');
    setOtpCode('');
    setPassword('');
    setNewPassword('');
    setShowOtpInput(false);
    setShowPasswordInput(false);
    setError('');
    setSuccess('');
    setIsLoading(false);
    setResettingPassword(false);
  };

  const handleToggleForgotPasswordMode = () => {
    setIsForgotPasswordMode(true);
    setIsLoginMode(false);
    setPhoneNumber('');
    setOtpCode('');
    setPassword('');
    setNewPassword('');
    setShowOtpInput(false);
    setShowPasswordInput(false);
    setError('');
    setSuccess('');
    setIsLoading(false);
    setResettingPassword(false);
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        {/* Amazon Logo Area */}
        <div className="text-center mb-6">
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight">
            {isLoginMode ? 'Sign in' : isForgotPasswordMode ? 'Password Reset' : 'Create Account'}
          </h1>
        </div>

        {/* Main Container with Amazon Border */}
        <div className="border border-gray-300 rounded-lg px-6 py-8 shadow-sm bg-white">

          {/* Login Mode */}
          {isLoginMode && (
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label htmlFor="phone" className="block text-sm font-bold text-gray-700 mb-1">
                  Mobile phone number
                </label>
                <input
                  id="phone"
                  type="tel"
                  placeholder="11-digit mobile number"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  required
                  disabled={isLoading}
                  className="w-full px-3 py-2 border border-gray-400 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
              </div>

              <div className="relative">
                <label htmlFor="password" className="block text-sm font-bold text-gray-700 mb-1">
                  Password
                </label>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={isLoading}
                    className="w-full px-3 py-2 pr-10 border border-gray-400 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />
                  <button
                    type="button"
                    onClick={togglePasswordVisibility}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-gradient-to-b from-[#f7dfa5] to-[#f0c14b] border border-[#a88734] hover:from-[#f5d78e] hover:to-[#e7b93e] text-gray-900 py-2 px-4 rounded-sm text-sm font-medium shadow-sm disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                {isLoading ? 'Signing in...' : 'Sign in'}
              </button>

              <div className="text-xs text-gray-700 leading-relaxed mt-4">
                By continuing, you agree to our Conditions of Use and Privacy Notice.
              </div>

              <div className="mt-4">
                <button
                  type="button"
                  onClick={handleToggleForgotPasswordMode}
                  className="text-sm text-blue-600 hover:text-orange-600 hover:underline"
                >
                  Forgot your password?
                </button>
              </div>
            </form>
          )}

          {/* Register Mode */}
          {!isLoginMode && !isForgotPasswordMode && (
            <>
              <form onSubmit={handleSendOtp} className="space-y-4">
                <div>
                  <label htmlFor="phone-register" className="block text-sm font-bold text-gray-700 mb-1">
                    Mobile phone number
                  </label>
                  <input
                    id="phone-register"
                    type="tel"
                    placeholder="11-digit mobile number"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    required
                    disabled={showOtpInput || showPasswordInput || isLoading}
                    className="w-full px-3 py-2 border border-gray-400 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />
                </div>
                <button
                  type="submit"
                  disabled={showOtpInput || showPasswordInput || isLoading}
                  className="w-full bg-gradient-to-b from-[#f7dfa5] to-[#f0c14b] border border-[#a88734] hover:from-[#f5d78e] hover:to-[#e7b93e] text-gray-900 py-2 px-4 rounded-sm text-sm font-medium shadow-sm disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  {isLoading ? 'Sending...' : 'Send OTP'}
                </button>
              </form>

              {showOtpInput && (
                <form onSubmit={handleVerifyOtp} className="space-y-4 mt-6 pt-6 border-t border-gray-300">
                  <div>
                    <label htmlFor="otp" className="block text-sm font-bold text-gray-700 mb-1">
                      Enter OTP code
                    </label>
                    <input
                      id="otp"
                      type="text"
                      placeholder="Your OTP code"
                      value={otpCode}
                      onChange={(e) => setOtpCode(e.target.value)}
                      required
                      disabled={showPasswordInput || isLoading}
                      className="w-full px-3 py-2 border border-gray-400 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={showPasswordInput || isLoading}
                    className="w-full bg-gradient-to-b from-[#f7dfa5] to-[#f0c14b] border border-[#a88734] hover:from-[#f5d78e] hover:to-[#e7b93e] text-gray-900 py-2 px-4 rounded-sm text-sm font-medium shadow-sm disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    {isLoading ? 'Verifying...' : 'Verify OTP'}
                  </button>
                </form>
              )}

              {showPasswordInput && (
                <>
                  <div className="mt-6 pt-6 border-t border-gray-300">
                    <p className="text-sm font-medium text-gray-700 mb-4">Complete your registration:</p>
                    <form className="space-y-4">
                      <div className="relative">
                        <label htmlFor="set-password" className="block text-sm font-bold text-gray-700 mb-1">
                          Set your password
                        </label>
                        <div className="relative">
                          <input
                            id="set-password"
                            type={showNewPassword ? "text" : "password"}
                            placeholder="Enter your password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            disabled={isLoading}
                            className="w-full px-3 py-2 pr-10 border border-gray-400 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                          />
                          <button
                            type="button"
                            onClick={toggleNewPasswordVisibility}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                          >
                            {showNewPassword ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
                          </button>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => handleCompleteRegistration(e, false)}
                        disabled={isLoading}
                        className="w-full bg-gradient-to-b from-[#f7dfa5] to-[#f0c14b] border border-[#a88734] hover:from-[#f5d78e] hover:to-[#e7b93e] text-gray-900 py-2 px-4 rounded-sm text-sm font-medium shadow-sm disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                      >
                        {isLoading ? 'Registering...' : 'Register with password'}
                      </button>
                      <div className="relative my-4">
                        <div className="absolute inset-0 flex items-center">
                          <div className="w-full border-t border-gray-300"></div>
                        </div>
                        <div className="relative flex justify-center text-sm">
                          <span className="px-2 bg-white text-gray-500">or</span>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => handleCompleteRegistration(e, true)}
                        disabled={isLoading}
                        className="w-full bg-white border border-gray-400 hover:bg-gray-50 text-gray-900 py-2 px-4 rounded-sm text-sm font-medium shadow-sm disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                      >
                        {isLoading ? 'Registering...' : 'Register with auto-generated password'}
                      </button>
                    </form>
                  </div>
                </>
              )}
            </>
          )}

          {/* Forgot Password Mode */}
          {isForgotPasswordMode && (
            <>
              <form onSubmit={handleForgotPasswordSendOtp} className="space-y-4">
                <div>
                  <label htmlFor="phone-reset" className="block text-sm font-bold text-gray-700 mb-1">
                    Mobile phone number
                  </label>
                  <input
                    id="phone-reset"
                    type="tel"
                    placeholder="11-digit mobile number"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    required
                    disabled={showOtpInput || isLoading}
                    className="w-full px-3 py-2 border border-gray-400 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />
                </div>
                <button
                  type="submit"
                  disabled={showOtpInput || isLoading}
                  className="w-full bg-gradient-to-b from-[#f7dfa5] to-[#f0c14b] border border-[#a88734] hover:from-[#f5d78e] hover:to-[#e7b93e] text-gray-900 py-2 px-4 rounded-sm text-sm font-medium shadow-sm disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  {isLoading ? 'Sending OTP...' : 'Send OTP'}
                </button>
              </form>

              {showOtpInput && (
                <form onSubmit={handleResetPasswordVerifyOtp} className="space-y-4 mt-6 pt-6 border-t border-gray-300">
                  <div>
                    <label htmlFor="otp-reset" className="block text-sm font-bold text-gray-700 mb-1">
                      Enter OTP code
                    </label>
                    <input
                      id="otp-reset"
                      type="text"
                      placeholder="Your OTP code"
                      value={otpCode}
                      onChange={(e) => setOtpCode(e.target.value)}
                      required
                      disabled={resettingPassword}
                      className="w-full px-3 py-2 border border-gray-400 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    />
                  </div>
                  <div className="relative">
                    <label htmlFor="new-password" className="block text-sm font-bold text-gray-700 mb-1">
                      New password
                    </label>
                    <div className="relative">
                      <input
                        id="new-password"
                        type={showNewPassword ? "text" : "password"}
                        placeholder="Enter your new password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        required
                        disabled={resettingPassword}
                        className="w-full px-3 py-2 pr-10 border border-gray-400 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                      />
                      <button
                        type="button"
                        onClick={toggleNewPasswordVisibility}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      >
                        {showNewPassword ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
                      </button>
                    </div>
                  </div>
                  <button
                    type="submit"
                    disabled={resettingPassword}
                    className="w-full bg-gradient-to-b from-[#f7dfa5] to-[#f0c14b] border border-[#a88734] hover:from-[#f5d78e] hover:to-[#e7b93e] text-gray-900 py-2 px-4 rounded-sm text-sm font-medium shadow-sm disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    {resettingPassword ? 'Resetting password...' : 'Reset password'}
                  </button>
                </form>
              )}
            </>
          )}

          {/* Error and Success Messages */}
          {error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-300 rounded-sm">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}
          {success && (
            <div className="mt-4 p-3 bg-green-50 border border-green-300 rounded-sm">
              <p className="text-sm text-green-800">{success}</p>
            </div>
          )}
          {(isLoading || resettingPassword) && (
            <div className="mt-4 p-3 bg-blue-50 border border-blue-300 rounded-sm">
              <p className="text-sm text-blue-800">Please wait...</p>
            </div>
          )}
        </div>

        {/* Toggle Mode Section */}
        <div className="mt-6">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">New to our site?</span>
            </div>
          </div>
          <button
            onClick={handleToggleMode}
            className="mt-4 w-full bg-white border border-gray-400 hover:bg-gray-50 text-gray-900 py-2 px-4 rounded-sm text-sm font-medium shadow-sm transition-all"
          >
            {isLoginMode && !isForgotPasswordMode ? (
              'Create your account'
            ) : (
              'Sign in to your account'
            )}
          </button>
        </div>

        {/* Footer Links */}
        <div className="mt-8 text-center">
          <div className="flex justify-center space-x-4 text-xs text-blue-600">
            <a href="#" className="hover:text-orange-600 hover:underline">Conditions of Use</a>
            <a href="#" className="hover:text-orange-600 hover:underline">Privacy Notice</a>
            <a href="#" className="hover:text-orange-600 hover:underline">Help</a>
          </div>
          <p className="mt-4 text-xs text-gray-600">© 2019-2026, {AppConfig.CompanyName} or its affiliates</p>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;